import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useTheme } from '../contexts/ThemeContext';
import { useRadio } from '../contexts/RadioContext';
import '../styles/GlobeMap.css';
import '../styles/PlayerEffects.css';

// Set Mapbox token from environment variable
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

// Validate token is present
if (!mapboxgl.accessToken) {
  throw new Error('Mapbox token is required. Please set REACT_APP_MAPBOX_TOKEN in your environment variables.');
}

const GlobeMap = ({ stations }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const popupRef = useRef(null);
  const { darkMode } = useTheme();
  const { playStation, stationLoading } = useRadio();
  const [loadingStationId, setLoadingStationId] = useState(null);
  
  // Initialize map on first render
  useEffect(() => {
    if (map.current) return; // initialize map only once

    try {
      // Create the map with basic configuration
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: darkMode ? 'mapbox://styles/mapbox/dark-v10' : 'mapbox://styles/mapbox/light-v10',
        center: [0, 20],
        zoom: 2,
        pitch: 30, // Enable pitch for 3D globe view
        bearing: 0, 
        antialias: true,
        renderWorldCopies: true,
        dragRotate: true, // Enable rotation for globe
        boxZoom: true,
        doubleClickZoom: true,
        attributionControl: false, // We'll add this manually in a better position
        fadeDuration: 0, // Disable fade animations for better performance
        preserveDrawingBuffer: true,
        maxBounds: [[-180, -85], [180, 85]] // Limit panning to prevent extreme positions
      });

      // Add navigation controls with compass for rotation
      map.current.addControl(new mapboxgl.NavigationControl({
        showCompass: true // Show the compass for rotation
      }), 'bottom-right');
      
      // Add attribution in a better position
      map.current.addControl(new mapboxgl.AttributionControl({
        compact: true
      }), 'bottom-left');

      // When the style loads, setup the map projection
      map.current.on('load', () => {
        setupGlobeProjection();
      });
      
      // Fix for choppy performance
      map.current.on('render', () => {
        if (map.current) {
          map.current.resize();
        }
      });
      
    } catch (err) {
    }

    // Cleanup function to remove map when component unmounts
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Handle theme changes
  useEffect(() => {
    if (!map.current) return;
    
    const newStyleUrl = darkMode 
      ? 'mapbox://styles/mapbox/dark-v10' 
      : 'mapbox://styles/mapbox/light-v10';
    
    try {
      // Only change style if map is fully loaded
      if (map.current.loaded()) {
        map.current.setStyle(newStyleUrl);
        
        // Need to re-setup globe after style changes
        map.current.once('style.load', setupGlobeProjection);
      }
    } catch (err) {
    }
  }, [darkMode]);

  // Add markers when stations data changes
  useEffect(() => {
    if (!map.current || !stations || stations.length === 0) return;
    
    // Add markers once map is ready
    if (map.current.loaded()) {
      setTimeout(addStationMarkers, 100); // Small delay to ensure map is ready
    } else {
      map.current.once('load', () => {
        setTimeout(addStationMarkers, 100);
      });
    }
  }, [stations]);

  // Setup globe projection and related settings
  const setupGlobeProjection = () => {
    if (!map.current) return;
    
    try {
      // Use globe projection for 3D effect
      map.current.setProjection('globe');
      
      // Add atmosphere-like effects for the globe
      map.current.setFog({
        color: darkMode ? 'rgb(10, 10, 40)' : 'rgb(255, 255, 255)',
        'high-color': darkMode ? 'rgb(36, 92, 223)' : 'rgb(186, 210, 235)',
        'horizon-blend': 0.15,
        'space-color': darkMode ? '#000' : '#dce9ff',
        'star-intensity': 0.5
      });
      
      // Enable some pitch and bearing for 3D feel
      map.current.setPitch(30);
      map.current.setBearing(0);
      
      // Allow rotation for the globe experience
      map.current.dragRotate.enable();
      map.current.touchZoomRotate.enableRotation();
      
      // Add station markers
      addStationMarkers();
    } catch (err) {
    }
  };

  // Add station markers to the map
  const addStationMarkers = () => {
    if (!map.current || !stations) return;
    
    try {      
      // Remove existing markers
      const existingMarkers = document.querySelectorAll('.mapboxgl-marker');
      existingMarkers.forEach(marker => marker.remove());

      // Add station markers
      stations.forEach(station => {
        if (!station.longitude || !station.latitude) {
          return;
        }

        // Convert coordinates to numbers to ensure proper positioning
        const lng = parseFloat(station.longitude);
        const lat = parseFloat(station.latitude);
        
        if (isNaN(lng) || isNaN(lat)) {
          return;
        }

        // Create marker element
        const markerEl = document.createElement('div');
        markerEl.className = 'station-marker';
        markerEl.style.backgroundColor = getGenreColor(station.genres?.[0]);
        markerEl.setAttribute('data-station-id', station.id);
        
        // Create marker and add it to the map
        const marker = new mapboxgl.Marker({
          element: markerEl,
          anchor: 'center'
        })
        .setLngLat([lng, lat])
        .addTo(map.current);
        
        // Create popup but don't add it to the map yet
        const popup = new mapboxgl.Popup({
          offset: 25,
          closeButton: true,
          closeOnClick: true, // Close when map is clicked
          anchor: 'bottom',
          maxWidth: '300px'
        })
        .setHTML(`
          <div class="station-popup">
            <h3>${station.name}</h3>
            <p>${station.country}${station.city ? `, ${station.city}` : ''}</p>
            <p class="station-genre">${station.genres?.join(', ') || 'Various'}</p>
            <div style="text-align: left; width: 100%; padding: 0; margin: 0;">
              <button class="play-station-btn" data-station-id="${station.id}" style="margin-left: 0 !important; margin-right: auto; display: block;">
                <span class="play-text">Load Station</span>
              </button>
            </div>
          </div>
        `);
        
        // Add click event to marker to show popup
        markerEl.addEventListener('click', (e) => {
          e.stopPropagation();
          
          // Remove any existing popups
          document.querySelectorAll('.mapboxgl-popup').forEach(p => p.remove());
          
          // Add popup to marker
          marker.setPopup(popup);
          popup.addTo(map.current);
          
          // Add click handler to the play button
          setTimeout(() => {
            const playBtn = document.querySelector(`.play-station-btn[data-station-id="${station.id}"]`);
            if (playBtn) {
              // Remove any existing event listeners
              const newPlayBtn = playBtn.cloneNode(true);
              playBtn.parentNode.replaceChild(newPlayBtn, playBtn);
              
              newPlayBtn.addEventListener('click', async (e) => {
                e.stopPropagation();
                
                // Show loading animation on the marker
                setLoadingStationId(station.id);
                markerEl.innerHTML = `<div class="map-loading-overlay"><div class="map-station-loader"></div></div>`;
                
                try {
                  // Play station
                  await playStation(station);
                } finally {
                  // Remove loading state whether playback succeeded or failed
                  // Use a longer timeout to ensure the state has properly updated
                  setTimeout(() => {
                    setLoadingStationId(null);
                    markerEl.innerHTML = '';
                    popup.remove();
                  }, 1000);
                }
              });
            }
          }, 50);
        });
        
        // Update marker with loading animation if this station is loading
        if (loadingStationId === station.id && stationLoading) {
          markerEl.innerHTML = `<div class="map-loading-overlay"><div class="map-station-loader"></div></div>`;
        } else {
          markerEl.innerHTML = '';
        }
      });
      
    } catch (err) {
    }
  };

  // Watch for loading state changes to update markers
  useEffect(() => {
    if (map.current && map.current.loaded()) {
      // Only update markers if we have a loadingStationId
      if (loadingStationId) {
        const markerEl = document.querySelector(`.station-marker[data-station-id="${loadingStationId}"]`);
        
        if (markerEl) {
          if (stationLoading) {
            // Show loading animation
            markerEl.innerHTML = `<div class="map-loading-overlay"><div class="map-station-loader"></div></div>`;
          } else {
            markerEl.innerHTML = '';
            setLoadingStationId(null); // Clear the loading station id
          }
        }
      }
    }
  }, [stationLoading, loadingStationId]);

  // Add a safety timeout to clear any stuck loading animations after 10 seconds
  useEffect(() => {
    let timeoutId;
    if (loadingStationId) {
      timeoutId = setTimeout(() => {
        const markerEl = document.querySelector(`.station-marker[data-station-id="${loadingStationId}"]`);
        if (markerEl) {
          markerEl.innerHTML = '';
        }
        setLoadingStationId(null);
      }, 10000);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loadingStationId]);

  // Get color based on genre
  const getGenreColor = (genre) => {
    if (!genre) return '#3498db'; // Default blue
    
    const genreColors = {
      'pop': '#e74c3c',      // Red
      'rock': '#9b59b6',     // Purple
      'jazz': '#f1c40f',     // Yellow
      'classical': '#1abc9c', // Turquoise
      'electronic': '#2ecc71', // Green
      'hiphop': '#e67e22',   // Orange
      'news': '#34495e',     // Dark blue
      'talk': '#7f8c8d',     // Gray
    };
    
    const normalizedGenre = genre.toLowerCase();
    return genreColors[normalizedGenre] || '#3498db';
  };

  // Add a global click handler to close popups when clicking outside
  useEffect(() => {
    const handleMapClick = (e) => {
      // Only close if the click is on the map, not on a marker or popup
      if (!e.originalEvent.defaultPrevented && 
          !e.originalEvent.target.closest('.mapboxgl-popup') &&
          !e.originalEvent.target.closest('.station-marker')) {
        document.querySelectorAll('.mapboxgl-popup').forEach(p => p.remove());
      }
    };

    if (map.current) {
      map.current.on('click', handleMapClick);
    }

    return () => {
      if (map.current) {
        map.current.off('click', handleMapClick);
      }
    };
  }, [map.current]);

  return (
    <div className="globe-container">
      <div ref={mapContainer} className="map-container" />
    </div>
  );
};

export default GlobeMap; 